import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { definePersistenceEntry } from '../persistence/registry'
import type { ModulePersistenceEntry, SliceDeclaration } from '../persistence/types'

/**
 * Состояние режима обновления РМК, как его отдаёт сервер (RP-MNT, `GET /api/platform/v1/maintenance`).
 * Источник истины — сервер; живой канал только уведомляет «перечитай».
 */
export interface MaintenanceDto {
  /** Включён ли режим обновления. */
  active: boolean
  /** Текст кассирам (дословно, ADR-8) или `null`: тогда показывается общий текст. */
  message: string | null
  /** Момент включения (ISO) или `null`. */
  since: string | null
}

/**
 * Режим обновления глазами оболочки. Срок жизни — `device`: держится на устройстве, чтобы перезагрузка страницы во время
 * перезапуска сервера (когда он недоступен) не выглядела аварией, а осталась «идёт обновление». Срок годности записи
 * ограничивает устаревание: включённый неделю назад режим из localStorage кассу не запрёт — свежее состояние придёт
 * первым же ответом сервера.
 */
export interface MaintenanceState extends MaintenanceDto {
  /** Когда (мс) клиент в последний раз получил состояние от сервера; `null` — ещё ни разу с открытия страницы. */
  knownAt: number | null
}

export const MAINTENANCE_INITIAL: MaintenanceState = { active: false, message: null, since: null, knownAt: null }

const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState: MAINTENANCE_INITIAL,
  reducers: {
    /** Сервер назвал текущее состояние режима (ответ запроса или восстановление с устройства). */
    maintenanceObserved(state, action: PayloadAction<{ state: MaintenanceDto; at: number }>) {
      state.active = action.payload.state.active
      state.message = action.payload.state.message
      state.since = action.payload.state.since
      state.knownAt = action.payload.at
    },
  },
})

export const { maintenanceObserved } = maintenanceSlice.actions
export const maintenanceReducer = maintenanceSlice.reducer

export const selectMaintenance = (state: MaintenanceState): MaintenanceState => state
export const selectMaintenanceActive = (state: MaintenanceState): boolean => state.active

/** Объявление слайса для реестра хранения: держится на устройстве. */
export const MAINTENANCE_SLICES: readonly SliceDeclaration[] = [
  {
    name: 'maintenance',
    lifetime: 'device',
    reason: 'режим обновления РМК: F5 во время перезапуска сервера не должен выглядеть аварией; ttl ограничивает устаревание',
  },
]

/** Что из состояния режима обновления хранится на устройстве. */
function decodeMaintenancePersisted(raw: unknown): MaintenanceDto | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const value = raw as Record<string, unknown>
  if (typeof value.active !== 'boolean') return undefined
  const message = value.message === null || typeof value.message === 'string' ? (value.message as string | null) : undefined
  const since = value.since === null || typeof value.since === 'string' ? (value.since as string | null) : undefined
  if (message === undefined || since === undefined) return undefined
  return { active: value.active, message, since }
}

export const MAINTENANCE_PERSISTENCE: readonly ModulePersistenceEntry<MaintenanceState>[] = [
  definePersistenceEntry<MaintenanceState, MaintenanceDto>({
    key: 'maintenance.state',
    slice: 'maintenance',
    lifetime: 'device',
    scope: 'device',
    version: 1,
    // Режим обновления идёт минутами: двух часов хватает пережить перезапуск, но брошенный флаг не запрёт кассу навсегда.
    ttlMs: 2 * 60 * 60 * 1000,
    pick: (state) => ({ active: state.active, message: state.message, since: state.since }),
    decode: decodeMaintenancePersisted,
    restore: (value) => maintenanceObserved({ state: value, at: Date.now() }),
  }),
]
